import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  Bot, MessageCircle, Zap, Send, BarChart3, Users, ArrowRight, CheckCircle2, Sparkles,
} from "lucide-react";

const FEATURES = [
  { icon: Sparkles, title: "AI Chatbot", desc: "Gemini-powered replies that understand your customers and your knowledge base." },
  { icon: MessageCircle, title: "WhatsApp Simulator", desc: "Test conversations in an authentic WhatsApp-style chat before going live." },
  { icon: Zap, title: "Auto-reply Rules", desc: "Instant keyword responses + a smart knowledge base fallback." },
  { icon: Send, title: "Broadcast Campaigns", desc: "Send personalized bulk messages to tagged audience segments." },
  { icon: Users, title: "Contact Management", desc: "Organize contacts with tags, groups and quick filters." },
  { icon: BarChart3, title: "Live Analytics", desc: "Track message velocity, bot resolution and intent distribution." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-200 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" />

      {/* nav */}
      <header className="relative z-10 max-w-7xl mx-auto flex items-center justify-between px-6 h-20">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <Bot className="w-5 h-5 text-[#0B0F17]" />
          </div>
          <span className="font-display font-extrabold text-xl tracking-tight text-white">
            DVEPL
          </span>
        </div>
        <div className="flex items-center gap-3">
          {user ? (
            <Button data-testid="landing-dashboard-btn" onClick={() => navigate("/app")}
              className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-semibold">
              Dashboard <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <>
              <Link to="/login" data-testid="landing-login-link"
                className="text-sm font-medium text-slate-300 hover:text-white px-4 transition-colors">
                Log in
              </Link>
              <Button data-testid="landing-signup-btn" onClick={() => navigate("/signup")}
                className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-semibold">
                Get Started
              </Button>
            </>
          )}
        </div>
      </header>

      {/* hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-14 items-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-400 typing-dot" />
            <span className="text-xs font-mono uppercase tracking-wider text-emerald-400">AI + WhatsApp Automation</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
            Automate WhatsApp with your own <span className="text-emerald-400">AI chatbot</span>.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-lg leading-relaxed">
            Build auto-reply rules, train a knowledge base, run broadcast campaigns and test everything in a realistic WhatsApp simulator — all in one command center.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button data-testid="hero-start-btn" onClick={() => navigate(user ? "/app" : "/signup")}
              className="rounded-full h-12 px-7 bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-semibold text-base">
              Start building free <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/login")}
              className="rounded-full h-12 px-7 border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200 text-base">
              Live demo
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
            {["No credit card", "Instant setup", "Demo mode included"].map((t) => (
              <span key={t} className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {t}
              </span>
            ))}
          </div>
        </div>

        {/* mock chat card */}
        <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="glass rounded-3xl p-5 shadow-2xl shadow-emerald-500/5">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-700/50">
              <div className="w-11 h-11 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Bot className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="font-semibold text-white text-sm">DVEPL Bot</p>
                <p className="text-xs text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> online
                </p>
              </div>
            </div>
            <div className="space-y-3 py-5">
              <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-[#202c33] px-4 py-2.5 text-sm text-slate-200">
                Hi! Do you offer refunds?
              </div>
              <div className="max-w-[85%] ml-auto rounded-2xl rounded-tr-sm bg-[#005c4b] px-4 py-2.5 text-sm text-white">
                Yes! You can request a full refund within 14 days. Refunds are processed in 5–7 business days. 💚
              </div>
              <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-[#202c33] px-4 py-2.5 text-sm text-slate-200">
                What are your pricing plans?
              </div>
              <div className="flex items-center gap-1.5 pl-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 typing-dot" />
                <span className="w-2 h-2 rounded-full bg-emerald-400 typing-dot" style={{ animationDelay: "0.2s" }} />
                <span className="w-2 h-2 rounded-full bg-emerald-400 typing-dot" style={{ animationDelay: "0.4s" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* features */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-28">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white text-center">
          Everything you need to automate
        </h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={f.title}
              className="glass rounded-2xl p-6 hover:border-emerald-500/30 transition-colors duration-300 animate-fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}>
              <div className="w-11 h-11 rounded-xl bg-emerald-500/12 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-display font-semibold text-lg text-white">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* footer */}
      <footer className="relative z-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-slate-500">© {new Date().getFullYear()} DV Electromatic Pvt Ltd (DVEPL).</span>
          <Link to="/privacy-policy" data-testid="landing-privacy-link"
            className="text-xs text-slate-400 hover:text-emerald-400 transition-colors">
            Privacy Policy
          </Link>
        </div>
      </footer>
    </div>
  );
}
